import Vue from 'vue';
import axios from 'axios';
import assign from 'lodash/assign';
import {injectGoalIfScoreChanged} from '@/services/goalDetector';
import {reapplyKeyOnMarkets} from '@/services/keyLiveReapply.service';

// version 3.3 + Monotonic Shield (Status & lastUpdate)

// --- CONSTANTS ---
const EVENT_STATUS = {
  PREMATCH: 1,
  LIVE: 2,
  FINISHED: 3,
  STARTING_SOON: 9
};

// ── Lookup tables for socket updates (mixedPreview.liveEvents) ───────────────
let mixedLiveEventsLookup = {};
let mixedLiveOddsLookup = {};

// --- GOAL BADGE TIMERS ---
const _goalTimers = new Map();

// --- TYPE CHANGE RESET TIMER ---
let _typeChangeResetTimer = null;
const TYPE_CHANGE_RESET_DELAY = 2500;

function _scheduleTypeChangeReset(commit) {
  if (_typeChangeResetTimer) clearTimeout(_typeChangeResetTimer);
  _typeChangeResetTimer = setTimeout(() => {
    commit('RESET_TYPE_CHANGES');
    _typeChangeResetTimer = null;
  }, TYPE_CHANGE_RESET_DELAY);
}

// --- HELPER: MONOTONIC UPDATE ---
function extractLastUpdate(payload) {
  if (!payload) return 0;
  if (payload.lastUpdate) return new Date(payload.lastUpdate).getTime();
  if (payload.livescore) {
    const ls = Array.isArray(payload.livescore) ? payload.livescore[0] : payload.livescore;
    if (ls && ls.lastUpdate) return new Date(ls.lastUpdate).getTime();
  }
  return 0;
}

function isPayloadStale(existing, incoming) {
  if (!existing || !incoming) return false;

  const exStatus = parseInt(existing.status || existing.livescore?.scoreboard?.status || '0');
  let inStatus = parseInt(incoming.status || '0');
  if (inStatus === 0 && incoming.livescore) {
    const inLs = Array.isArray(incoming.livescore) ? incoming.livescore[0] : incoming.livescore;
    inStatus = parseInt(inLs?.scoreboard?.status || '0');
  }

  if ((inStatus === EVENT_STATUS.PREMATCH || inStatus === EVENT_STATUS.STARTING_SOON) &&
      (exStatus === EVENT_STATUS.LIVE || exStatus === EVENT_STATUS.FINISHED)) {
    return true;
  }
  if (inStatus === EVENT_STATUS.LIVE && exStatus === EVENT_STATUS.FINISHED) {
    return true;
  }

  const exTime = extractLastUpdate(existing);
  const inTime = extractLastUpdate(incoming);

  if (exTime > 0 && inTime > 0 && inTime < exTime) return true;

  return false;
}

function mapMixedLiveMarketToLookup(market, eventId) {
  if (!market || !market.outcome || !Array.isArray(market.outcome)) return;
  for (let k = 0; k < market.outcome.length; k++) {
    const odd = market.outcome[k];
    if (odd.unique_id) {
      mixedLiveOddsLookup[odd.unique_id] = {odd, market, eventId: eventId || null};
    }
  }
}

// ── Merge helper ────────────────────────────────────────────────────────────
function mergeNavigation(live, prematch) {
  const liveSports = live?.sportsAvailable ?? [];
  const prematchSports = prematch?.sportsAvailable ?? [];
  const liveCats = live?.categoriesBySport ?? {};
  const prematchCats = prematch?.categoriesBySport ?? {};
  const liveTmts = live?.tournamentsByCategory ?? {};
  const prematchTmts = prematch?.tournamentsByCategory ?? {};

  const sportsMap = new Map();
  for (const s of prematchSports) {
    if (s?.id != null) {
      sportsMap.set(String(s.id), {...s, live: false});
    }
  }
  for (const s of liveSports) {
    if (s?.id != null) {
      const key = String(s.id);
      sportsMap.set(
          key,
          sportsMap.has(key)
              ? {...sportsMap.get(key), ...s, live: true}
              : {...s, live: true}
      );
    }
  }

  const sports = [...sportsMap.values()].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const categoriesBySport = {};
  const allSportIds = new Set([...Object.keys(liveCats), ...Object.keys(prematchCats)]);

  for (const sportId of allSportIds) {
    const catMap = new Map();
    for (const c of prematchCats[sportId] ?? []) {
      if (c?.categoryId != null) catMap.set(String(c.categoryId), {...c, live: false});
    }
    for (const c of liveCats[sportId] ?? []) {
      if (c?.categoryId != null) {
        const key = String(c.categoryId);
        catMap.set(key, catMap.has(key) ? {...catMap.get(key), ...c, live: true} : {...c, live: true});
      }
    }
    categoriesBySport[sportId] = [...catMap.values()].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  const tournamentsByCat = {};
  const allCatIds = new Set([...Object.keys(liveTmts), ...Object.keys(prematchTmts)]);

  for (const catId of allCatIds) {
    const tmtMap = new Map();
    for (const t of prematchTmts[catId] ?? []) {
      if (t?.tournamentId != null) tmtMap.set(String(t.tournamentId), {...t, live: false});
    }
    for (const t of liveTmts[catId] ?? []) {
      if (t?.tournamentId != null) {
        const key = String(t.tournamentId);
        tmtMap.set(key, tmtMap.has(key) ? {...tmtMap.get(key), ...t, live: true} : {...t, live: true});
      }
    }
    tournamentsByCat[catId] = [...tmtMap.values()].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  return {sports, categoriesBySport, tournamentsByCat};
}

// ── State ────────────────────────────────────────────────────────────────────
const state = {
  sports: [],
  categoriesBySport: {},
  tournamentsByCat: {},
  activeSportId: null,
  openSportId: null,
  expandedCatIdsBySport: {},
  tabBySport: {},
  loadingCatIds: [],
  loading: false,
  mixedPreview: null,
  mixedPreviewLoading: false,
  mixedPreviewPage: 1,
  mixedPreviewHasMore: false,
  mixedPreviewNextPageLoading: false,
};

// ── Getters ──────────────────────────────────────────────────────────────────
const getters = {
  getSports: s => s.sports,
  getCategoriesBySport: s => s.categoriesBySport,
  getTournamentsByCat: s => s.tournamentsByCat,
  getActiveSportId: s => s.activeSportId,
  getOpenSportId: s => s.openSportId,
  getExpandedCatIds: s => s.expandedCatIdsBySport[String(s.openSportId)] || [],
  getTabBySport: s => s.tabBySport,
  getLoadingCatIds: s => s.loadingCatIds,
  getLoading: s => s.loading,
  getMixedPreview: s => s.mixedPreview,
  getMixedPreviewLoading: s => s.mixedPreviewLoading,
  getMixedPreviewHasMore: s => s.mixedPreviewHasMore,
  getMixedPreviewNextPageLoading: s => s.mixedPreviewNextPageLoading,
  getSortedSports: s => [...s.sports].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
  getActiveSportCategories: s => {
    if (!s.openSportId) return [];
    return (s.categoriesBySport[s.openSportId] || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  },
};

// ── Actions ──────────────────────────────────────────────────────────────────
const actions = {
  async fetchSportsNavigation({commit, getters: g, rootGetters}) {
    if (rootGetters['ui/getAppSection'] === 'live') return;
    commit('SET_LOADING', true);
    try {
      const urlApi = rootGetters['settings/getUrlApi'];
      const token = rootGetters['settings/getToken'];
      const platformId = rootGetters['settings/getPlatformId'];
      const language = rootGetters['settings/getLanguage'];

      const {data} = await axios.post(`${urlApi}/v3/navigation/unified`, {
        platformId,
        language
      }, {headers: {Authorization: `Bearer ${token}`}});
      const result = data?.result ?? data;
      let sports = [], categoriesBySport = {}, tournamentsByCat = {};

      if (result?.live || result?.prematch) {
        const merged = mergeNavigation(result.live ?? {}, result.prematch ?? {});
        sports = merged.sports;
        categoriesBySport = merged.categoriesBySport;
        tournamentsByCat = merged.tournamentsByCat;
      } else {
        sports = result?.sportsAvailable ?? [];
        categoriesBySport = result?.categoriesBySport ?? {};
      }

      commit('SET_SPORTS', sports);
      commit('SET_CATEGORIES_BY_SPORT', categoriesBySport);
      commit('SET_TOURNAMENTS_BY_CAT', tournamentsByCat);

      const currentActive = g.getActiveSportId;
      const validCurrent = currentActive && sports.find(s => s.id === currentActive);
      const firstId = [...sports].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0]?.id ?? null;
      const restoredId = validCurrent ? currentActive : firstId;
      if (restoredId) commit('SET_ACTIVE_SPORT_ID', restoredId);
    } catch (e) {
      console.error('[topsports] fetchSportsNavigation error:', e);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchTournaments({commit, state: s, rootGetters}, {sportId, categoryId}) {
    if (s.loadingCatIds.includes(categoryId)) return;
    commit('ADD_LOADING_CAT_ID', categoryId);
    try {
      const urlApi = rootGetters['settings/getUrlApi'];
      const token = rootGetters['settings/getToken'];
      const language = rootGetters['settings/getLanguage'];
      const platformId = rootGetters['settings/getPlatformId'];
      const res = await axios.post(`${urlApi}/v3/navigation/tournaments`, {
        platformId,
        sportId,
        categoryId,
        lang: language,
        filter: 0
      }, {headers: {Authorization: `Bearer ${token}`}});

      const liveList = res?.data?.live ?? [], prematchList = res?.data?.prematch ?? [];
      const tmtMap = new Map();
      for (const t of prematchList) if (t?.tournamentId != null) tmtMap.set(String(t.tournamentId), {
        ...t,
        live: false
      });
      for (const t of liveList) {
        if (t?.tournamentId != null) {
          const key = String(t.tournamentId);
          tmtMap.set(key, tmtMap.has(key) ? {...tmtMap.get(key), ...t, live: true} : {...t, live: true});
        }
      }
      commit('SET_TOURNAMENT_FOR_CAT', {
        categoryId,
        tournaments: [...tmtMap.values()].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      });
    } catch (e) {
      console.error('[topsports] fetchTournaments error:', e);
      commit('SET_TOURNAMENT_FOR_CAT', {categoryId, tournaments: []});
    } finally {
      commit('REMOVE_LOADING_CAT_ID', categoryId);
    }
  },

  /** Force-open a sport panel without toggling (used by slug navigation restore). */
  openSport({commit}, sportId) {
    commit('SET_MIXED_PREVIEW', null);
    commit('SET_OPEN_SPORT_ID', sportId);
    commit('SET_TOURNAMENTS_BY_CAT', {});
    commit('SET_ACTIVE_SPORT_ID', sportId);
  },

  /** Expand a category if not already open; fetch tournaments if needed. */
  async ensureCategoryExpanded({commit, state: s, dispatch}, {sportId, categoryId}) {
    const current = s.expandedCatIdsBySport[String(sportId)] || [];
    if (current.includes(categoryId)) return;
    commit('TOGGLE_EXPANDED_CAT_ID', {sportId, categoryId});
    if (!s.tournamentsByCat[categoryId]) await dispatch('fetchTournaments', {sportId, categoryId});
  },

  toggleSport({commit, state: s}, sportId) {
    if (s.openSportId === sportId) commit('SET_OPEN_SPORT_ID', null);
    else {
      commit('SET_MIXED_PREVIEW', null);
      commit('SET_OPEN_SPORT_ID', sportId);
      commit('SET_TOURNAMENTS_BY_CAT', {});
    }
    commit('SET_ACTIVE_SPORT_ID', sportId);
  },

  closePanel({commit, dispatch}) {
    commit('SET_OPEN_SPORT_ID', null);
    commit('SET_MIXED_PREVIEW', null);
    dispatch('ui/setSubaction', 'home', {root: true});
  },

  setTabForSport({commit}, {sportId, tab}) {
    commit('SET_TAB_FOR_SPORT', {sportId, tab});
  },
  clearMixedPreview({commit}) {
    commit('SET_MIXED_PREVIEW', null);
  },

  async fetchMixedPreview({commit, rootGetters}, payload) {
    const limit = rootGetters['settings/getMaxMixedEventsForPage'];
    commit('SET_MIXED_PREVIEW', null);
    commit('SET_MIXED_PREVIEW_LOADING', true);
    try {
      const apiUrl = rootGetters['settings/getUrlApi'];
      const token = rootGetters['settings/getToken'];
      const platformId = rootGetters['settings/getPlatformId'];
      const language = rootGetters['settings/getLanguage'];

      const res = await axios.post(`${apiUrl}/v3/events/tournament/mixed-preview`, {
        platformId,
        tournamentId: payload.tournamentId,
        sportId: payload.sportId,
        categoryId: payload.categoryId ?? null,
        lang: language,
        groupId: payload.groupId ?? null,
        marketId: payload.marketId ?? null,
        mobilePlatform: false,
        filterEmptyOdds: false,
        page: 1,
        limit
      }, {headers: {Authorization: `Bearer ${token}`}});

      const result = res.data?.result ?? {};
      const allEvents = (result?.groupedEvents ?? []).flat();

      if (res.data.messages === 'Success Operation' && allEvents.length > 0) {
        const sportObj = {id: payload.sportId, name: res.data.sportName ?? payload.sportName ?? ''};
        const categoryObj = {id: payload.categoryId, name: res.data.categoryName ?? payload.categoryName ?? ''};
        const tournamentObj = {
          id: payload.tournamentId,
          name: res.data.tournamentName ?? payload.tournamentName ?? ''
        };

        const liveEventsFlat = allEvents.filter(e => e.matchType === 'live' || e.matchType === 'starting_soon');
        const prematchEvents = allEvents.filter(e => e.matchType !== 'starting_soon' && e.matchType !== 'live');

        const dateMap = new Map();
        for (const ev of [...prematchEvents].sort((a, b) => new Date(a.scheduled) - new Date(b.scheduled))) {
          const dateKey = ev.scheduled ? ev.scheduled.split('T')[0] : 'unknown';
          if (!dateMap.has(dateKey)) dateMap.set(dateKey, {scheduled: dateKey, events: []});
          dateMap.get(dateKey).events.push(ev);
        }
        const eventsObj = {};
        for (const [key, group] of [...dateMap.entries()].sort(([a], [b]) => a.localeCompare(b))) eventsObj[key] = group;

        commit('SET_MIXED_PREVIEW', {
          sport: sportObj,
          category: categoryObj,
          tournament: tournamentObj,
          events: eventsObj,
          liveEvents: liveEventsFlat,
          headerSign: result.headerSignPrematch ?? null,
          headerSignLive: result.headerSign ?? null,
        });
        commit('SET_MIXED_PREVIEW_PAGE', 1);
        commit('SET_MIXED_PREVIEW_HAS_MORE', allEvents.length >= limit);
      } else {
        commit('SET_MIXED_PREVIEW', null);
        commit('SET_MIXED_PREVIEW_HAS_MORE', false);
      }
    } catch (e) {
      console.error('[topsports] fetchMixedPreview error:', e);
      commit('SET_MIXED_PREVIEW', null);
      commit('SET_MIXED_PREVIEW_HAS_MORE', false);
    } finally {
      commit('SET_MIXED_PREVIEW_LOADING', false);
    }
  },

  async fetchMixedPreviewNextPage({commit, state: s, rootGetters}) {
    if (!s.mixedPreview || s.mixedPreviewNextPageLoading || !s.mixedPreviewHasMore) return;
    const limit = rootGetters['settings/getMaxMixedEventsForPage'];
    const nextPage = s.mixedPreviewPage + 1;
    commit('SET_MIXED_PREVIEW_NEXT_PAGE_LOADING', true);
    try {
      const apiUrl = rootGetters['settings/getUrlApi'];
      const token = rootGetters['settings/getToken'];
      const platformId = rootGetters['settings/getPlatformId'];
      const language = rootGetters['settings/getLanguage'];
      const preview = s.mixedPreview;

      const res = await axios.post(`${apiUrl}/v3/events/tournament/mixed-preview`, {
        platformId,
        tournamentId: preview.tournament.id,
        sportId: preview.sport.id,
        categoryId: preview.category.id ?? null,
        lang: language,
        mobilePlatform: false,
        filterEmptyOdds: false,
        page: nextPage,
        limit
      }, {headers: {Authorization: `Bearer ${token}`}});

      const result = res.data?.result ?? {};
      const allEvents = (result?.groupedEvents ?? []).flat();

      if (allEvents.length > 0) {
        const liveEventsNew = allEvents.filter(e => e.matchType === 'live' || e.matchType === 'starting_soon');
        const prematchEvents = allEvents.filter(e => e.matchType !== 'starting_soon' && e.matchType !== 'live');

        const dateMap = new Map();
        for (const ev of [...prematchEvents].sort((a, b) => new Date(a.scheduled) - new Date(b.scheduled))) {
          const dateKey = ev.scheduled ? ev.scheduled.split('T')[0] : 'unknown';
          if (!dateMap.has(dateKey)) dateMap.set(dateKey, {scheduled: dateKey, events: []});
          dateMap.get(dateKey).events.push(ev);
        }

        commit('APPEND_MIXED_PREVIEW_EVENTS', {dateMap, liveEventsNew});
        commit('SET_MIXED_PREVIEW_PAGE', nextPage);
        commit('SET_MIXED_PREVIEW_HAS_MORE', allEvents.length >= limit);
      } else {
        commit('SET_MIXED_PREVIEW_HAS_MORE', false);
      }
    } catch (e) {
      console.error('[topsports] fetchMixedPreviewNextPage error:', e);
    } finally {
      commit('SET_MIXED_PREVIEW_NEXT_PAGE_LOADING', false);
    }
  },

  async socketUpdateOdds(context, payload) {
    context.commit('UPDATE_ODDS_FROM_SOCKET', payload);
    _scheduleTypeChangeReset(context.commit);
  },
  async socketUpdateLivescore(context, payload) {
    context.commit('UPDATE_LIVESCORE_FROM_SOCKET', payload);
  },

  async toggleCategory({commit, state: s, dispatch}, {categoryId, sportId}) {
    const current = s.expandedCatIdsBySport[String(sportId)] || [];
    const isOpen = current.includes(categoryId);
    commit('TOGGLE_EXPANDED_CAT_ID', {sportId, categoryId});
    if (!isOpen && !s.tournamentsByCat[categoryId]) await dispatch('fetchTournaments', {sportId, categoryId});
  },
};

// ── Mutations ────────────────────────────────────────────────────────────────
const mutations = {
  SET_SPORTS(state, payload) {
    state.sports = payload;
  },
  SET_CATEGORIES_BY_SPORT(state, payload) {
    state.categoriesBySport = payload;
  },
  SET_TOURNAMENTS_BY_CAT(state, payload) {
    state.tournamentsByCat = payload;
  },
  SET_ACTIVE_SPORT_ID(state, payload) {
    state.activeSportId = payload;
  },
  SET_OPEN_SPORT_ID(state, payload) {
    state.openSportId = payload;
  },
  SET_LOADING(state, payload) {
    state.loading = payload;
  },
  SET_TAB_FOR_SPORT(state, {sportId, tab}) {
    Vue.set(state.tabBySport, String(sportId), tab);
  },
  SET_MIXED_PREVIEW(state, payload) {
    state.mixedPreview = payload;
    state.mixedPreviewPage = 1;
    state.mixedPreviewHasMore = false;
    state.mixedPreviewNextPageLoading = false;
    mixedLiveEventsLookup = {};
    mixedLiveOddsLookup = {};
    _goalTimers.forEach(timerId => clearTimeout(timerId));
    _goalTimers.clear();
    if (_typeChangeResetTimer) { clearTimeout(_typeChangeResetTimer); _typeChangeResetTimer = null; }
    if (!payload) return;
    const liveEvents = payload.liveEvents ?? [];
    for (let i = 0; i < liveEvents.length; i++) {
      const event = liveEvents[i];
      if (event?.id) {
        mixedLiveEventsLookup[event.id] = event;
        if (Array.isArray(event.odds)) {
          for (let k = 0; k < event.odds.length; k++) mapMixedLiveMarketToLookup(event.odds[k], event.id);
        }
      }
    }
  },
  SET_MIXED_PREVIEW_LOADING(state, payload) {
    state.mixedPreviewLoading = payload;
  },
  SET_MIXED_PREVIEW_PAGE(state, page) {
    state.mixedPreviewPage = page;
  },
  SET_MIXED_PREVIEW_HAS_MORE(state, val) {
    state.mixedPreviewHasMore = val;
  },
  SET_MIXED_PREVIEW_NEXT_PAGE_LOADING(state, val) {
    state.mixedPreviewNextPageLoading = val;
  },
  APPEND_MIXED_PREVIEW_EVENTS(state, {dateMap, liveEventsNew}) {
    if (!state.mixedPreview) return;
    // Merge prematch date buckets
    const events = {...state.mixedPreview.events};
    for (const [key, group] of [...dateMap.entries()].sort(([a], [b]) => a.localeCompare(b))) {
      if (events[key]) {
        events[key] = {...events[key], events: [...events[key].events, ...group.events]};
      } else {
        events[key] = group;
      }
    }
    Vue.set(state.mixedPreview, 'events', events);
    // Merge live events (deduplicate by id)
    if (liveEventsNew.length > 0) {
      const existingIds = new Set((state.mixedPreview.liveEvents ?? []).map(e => e.id));
      const uniqueNew = liveEventsNew.filter(e => !existingIds.has(e.id));
      if (uniqueNew.length > 0) {
        Vue.set(state.mixedPreview, 'liveEvents', [...(state.mixedPreview.liveEvents ?? []), ...uniqueNew]);
        for (const event of uniqueNew) {
          if (event?.id) {
            mixedLiveEventsLookup[event.id] = event;
            if (Array.isArray(event.odds)) {
              for (const market of event.odds) mapMixedLiveMarketToLookup(market, event.id);
            }
          }
        }
      }
    }
  },

  TOGGLE_EXPANDED_CAT_ID(state, {sportId, categoryId}) {
    const key = String(sportId);
    const current = state.expandedCatIdsBySport[key] || [];
    const idx = current.indexOf(categoryId);
    Vue.set(state.expandedCatIdsBySport, key, idx >= 0 ? current.filter(id => id !== categoryId) : [...current, categoryId]);
  },

  ADD_LOADING_CAT_ID(state, id) {
    state.loadingCatIds.push(id);
  },
  REMOVE_LOADING_CAT_ID(state, id) {
    const i = state.loadingCatIds.indexOf(id);
    if (i >= 0) state.loadingCatIds.splice(i, 1);
  },

  SET_TOURNAMENT_FOR_CAT(state, {categoryId, tournaments}) {
    Vue.set(state.tournamentsByCat, categoryId, tournaments);
  },

  UPDATE_ODDS_FROM_SOCKET(state, payload) {
    if (!payload || !Array.isArray(payload)) return;

    // Same dirty-set pattern as detailLive / previewLive.
    const dirtyMarkets = new Set();

    for (let i = 0; i < payload.length; i++) {
      const eventPayload = payload[i];
      if (!eventPayload.odds || !eventPayload.odds.set) continue;
      const oddChanges = eventPayload.odds.set;
      for (let j = 0; j < oddChanges.length; j++) {
        const changeItem = oddChanges[j];
        const uniqueId = changeItem.unique_id;
        const attr = changeItem._attributes;
        const lookupRef = mixedLiveOddsLookup[uniqueId];
        if (!lookupRef || !attr) continue;

        const targetOdd = lookupRef.odd;

        const inTime = attr.lastUpdate ? new Date(attr.lastUpdate).getTime() : 0;
        if (targetOdd.lastUpdate && inTime > 0 && inTime < targetOdd.lastUpdate) continue;

        const targetMarket = lookupRef.market;
        const newValue = parseFloat(attr.odds);
        const oldValue = parseFloat(targetOdd.outcome_odd_value);

        if (newValue > oldValue) Vue.set(targetOdd, 'type_change', 'up');
        else if (newValue < oldValue) Vue.set(targetOdd, 'type_change', 'down');

        // Socket carries the RAW provider value → track both baseline and
        // display. KEY reapply overwrites display when enabled.
        targetOdd.outcome_odd_original = newValue;
        targetOdd.outcome_odd_value = newValue;
        targetOdd.status = parseInt(attr.status);
        // Freeze-suspend authoritative — task 13.
        if (targetOdd.frozenApplied !== true) {
            targetOdd.active = (targetOdd.status === 1);
        }
        if (inTime > 0) targetOdd.lastUpdate = inTime;

        if (targetMarket && attr.line) targetMarket.specifier = attr.line;
        // R.1 early bailout — skip markets without KEY rule.
        if (targetMarket && targetMarket.keyApplied != null) dirtyMarkets.add(targetMarket);
      }
    }

    if (dirtyMarkets.size > 0) reapplyKeyOnMarkets(dirtyMarkets);
  },

  RESET_TYPE_CHANGES() {
    for (const key in mixedLiveOddsLookup) {
      const ref = mixedLiveOddsLookup[key];
      if (ref?.odd?.type_change != null) Vue.set(ref.odd, 'type_change', null);
    }
  },

  UPDATE_LIVESCORE_FROM_SOCKET(state, payload) {
    const updates = Array.isArray(payload) ? payload : [payload];
    for (let i = 0; i < updates.length; i++) {
      const change = updates[i];
      const event = mixedLiveEventsLookup[change.id];
      if (!event) continue;

      if (isPayloadStale(event, change)) continue;

      if (change.lastUpdate) Vue.set(event, 'lastUpdate', change.lastUpdate);
      if (change.status != null) event.status = String(change.status);

      if (change.livescore) {
        const incomingData = Array.isArray(change.livescore) ? change.livescore[0] : change.livescore;
        if (!incomingData) continue;

        const goalPosition = injectGoalIfScoreChanged(event, {...change, livescore: incomingData});
        if (goalPosition !== null) {
          if (_goalTimers.has(change.id)) clearTimeout(_goalTimers.get(change.id));
          const eventId = change.id;
          _goalTimers.set(eventId, setTimeout(() => {
            const evt = mixedLiveEventsLookup[eventId];
            if (evt?.livescore?.livescoreExtraData) {
              Vue.set(evt.livescore, 'livescoreExtraData', evt.livescore.livescoreExtraData.filter(e => e.type !== 9));
            }
            _goalTimers.delete(eventId);
          }, 10000));
        }

        if (!event.livescore) Vue.set(event, 'livescore', incomingData);
        else {
          if (incomingData.scoreboard) Vue.set(event.livescore, 'scoreboard', assign({}, event.livescore.scoreboard || {}, incomingData.scoreboard));
          if (incomingData.lastUpdate) Vue.set(event.livescore, 'lastUpdate', incomingData.lastUpdate);
          if (incomingData.livescoreExtraData) event.livescore.livescoreExtraData = incomingData.livescoreExtraData;
          if (incomingData.periods) Vue.set(event.livescore, 'periods', incomingData.periods);
          if (incomingData.statistics) Vue.set(event.livescore, 'statistics', incomingData.statistics);
          if (incomingData.matchStatus) Vue.set(event.livescore, 'matchStatus', incomingData.matchStatus);
        }
      }
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};